import React, { useState } from 'react';
import { API } from '../utils/api';
import axios from 'axios';
import ConfirmModal from './modals/ConfirmModal';
import SuccessModal from './modals/SuccessModal';
import ErrorModal from './modals/ErrorModal';

// SortIcon component moved outside to prevent re-creation on each render
const SortIcon = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) {
    return (
      <svg className="w-4 h-4 text-gray-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
        <path d="M5 12l5-5 5 5H5z"/>
      </svg>
    );
  }
  
  return sortDirection === 'asc' ? (
    <svg className="w-4 h-4 text-purple-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
      <path d="M5 12l5-5 5 5H5z"/>
    </svg>
  ) : (
    <svg className="w-4 h-4 text-purple-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
      <path d="M15 8l-5 5-5-5h10z"/>
    </svg>
  );
};

const DepartmentManagement = ({ departments, fetchDepartments }) => {
  const [showDeptForm, setShowDeptForm] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [sortField, setSortField] = useState('department_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [newDept, setNewDept] = useState({
    department_name: '', description: ''
  });
  const [editDept, setEditDept] = useState({
    department_name: '', description: ''
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedDepartments = () => {
    const filteredDepartments = departments.filter(dept => 
      dept.department_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (dept.description && dept.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    return filteredDepartments.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/departments`, newDept);
      setNewDept({ department_name: '', description: '' });
      setShowDeptForm(false);
      fetchDepartments();
      setSuccessModal({
        show: true,
        message: 'Department created successfully!'
      });
    } catch (error) {
      setErrorModal({
        show: true,
        message: 'Failed to create department: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const handleEditDepartment = (dept) => {
    setEditingDept(dept);
    setEditDept({
      department_name: dept.department_name,
      description: dept.description || ''
    });
  };

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    try {
      const updateData = {};
      if (editDept.department_name !== editingDept.department_name) updateData.department_name = editDept.department_name;
      if (editDept.description !== (editingDept.description || '')) updateData.description = editDept.description;

      await axios.put(`${API}/admin/departments/${editingDept.id}`, updateData);
      setEditingDept(null);
      setEditDept({ department_name: '', description: '' });
      fetchDepartments();
      setSuccessModal({
        show: true,
        message: 'Department updated successfully!'
      });
    } catch (error) {
      setErrorModal({
        show: true,
        message: 'Failed to update department: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const cancelEditDepartment = () => {
    setEditingDept(null);
    setEditDept({ department_name: '', description: '' });
  };

  const handleDeleteDepartment = (deptId) => {
    setConfirmModal({
      show: true,
      message: 'Are you sure you want to delete this department?',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/departments/${deptId}`);
          fetchDepartments();
          setSuccessModal({
            show: true,
            message: 'Department deleted successfully!'
          });
        } catch (error) {
          setErrorModal({
            show: true,
            message: 'Failed to delete department: ' + (error.response?.data?.detail || 'Unknown error')
          });
        }
      }
    });
  };

  return (
    <>
      <div className="bg-slate-800 rounded-xl border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700 bg-slate-700 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">All Departments</h2>
            <button
              onClick={() => setShowDeptForm(!showDeptForm)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-500 flex items-center transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"></path>
              </svg>
              {showDeptForm ? 'Cancel' : 'Add Department'}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-3 border-b border-slate-700">
          <div className="flex items-center">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <svg className="h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search departments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            {searchTerm && (
              <div className="ml-3 text-sm text-gray-400">
                {getSortedDepartments().length} of {departments.length} departments
              </div>
            )}
          </div>
        </div>

        {showDeptForm && (
          <div className="px-6 py-4 border-b border-slate-700 bg-slate-700/50">
            <form onSubmit={handleCreateDepartment} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Department Name"
                value={newDept.department_name}
                onChange={(e) => setNewDept({...newDept, department_name: e.target.value})}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <div className="md:col-span-2">
                <textarea
                  placeholder="Description (optional)"
                  value={newDept.description}
                  onChange={(e) => setNewDept({...newDept, description: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows="3"
                />
              </div>
              <button type="submit" className="md:col-span-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-500 transition-colors">
                Create Department
              </button>
            </form>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-600 bg-slate-700">
                <th 
                  className="text-left py-4 px-6 font-semibold text-gray-400 text-sm cursor-pointer hover:text-white select-none"
                  onClick={() => handleSort('department_name')}
                >
                  <div className="flex items-center">
                    DEPARTMENT NAME
                    <SortIcon field="department_name" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th className="text-left py-4 px-6 font-semibold text-gray-400 text-sm">DESCRIPTION</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-400 text-sm">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {getSortedDepartments().map((dept, index) => (
                <tr key={dept.id} className={`hover:bg-slate-700/50 transition-colors ${index !== getSortedDepartments().length - 1 ? "border-b border-slate-700/50" : ""}`}>
                  <td className="py-4 px-6">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"></path>
                        </svg>
                      </div>
                      <span className="font-medium text-white">{dept.department_name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-gray-300">{dept.description || <span className="text-gray-500 italic">No description</span>}</span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <button
                      onClick={() => handleEditDepartment(dept)}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-purple-300 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 mr-2 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteDepartment(dept.id)}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-red-300 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 transition-colors"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {getSortedDepartments().length === 0 && (
                <tr>
                  <td colSpan="3" className="py-8 text-center text-gray-500">
                    {searchTerm ? `No departments found matching "${searchTerm}"` : 'No departments found. Create a new department to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Department Modal */}
      {editingDept && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-slate-700">
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4 rounded-t-2xl">
              <h3 className="text-lg font-bold text-white">Edit Department: {editingDept.department_name}</h3>
            </div>
            <form onSubmit={handleUpdateDepartment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Department Name</label>
                <input
                  type="text"
                  value={editDept.department_name}
                  onChange={(e) => setEditDept({...editDept, department_name: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={editDept.description}
                  onChange={(e) => setEditDept({...editDept, description: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows="3"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  onClick={cancelEditDepartment}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg hover:from-purple-700 hover:to-indigo-800 transition-all"
                >
                  Update Department
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.show}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ show: false, message: '', onConfirm: null });
        }}
        onClose={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
      />

      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
    </>
  );
};

export default DepartmentManagement;
